// Code background: faint blocks of code (his stack: Spring Boot, Kafka,
// Angular, SQL, Docker...) type themselves out across the page.
// - Each block types a snippet with a blinking cursor, holds it, fades out and
//   starts another one, so the page always looks like work in progress.
// - Monochrome syntax shading: keywords and annotations darker, comments and
//   strings lighter. Code near the cursor darkens a little, like a flashlight.
// - Parallax between slides and the dark/light swap stay in CSS
//   (transform/filter on the canvas), so they never cost a redraw.
// - Some spots run a tiny C program instead: a while (i < 10) loop whose
//   current line lights up, and on each turn an address drops from the call
//   into the parameter of the function below.
// - The middle of the screen stays clear, so the slide's text is always readable.
// - With reduced motion the snippets are drawn once, fully typed, and stay still.
const FONT_SIZE = 12;
const LINE_HEIGHT = 18;
const BLOCK_WIDTH = 470; // px of canvas per block column
const TYPE_CPS = [18, 34]; // typing speed range, characters per second
const HOLD_MS = [4000, 9000]; // how long a finished snippet stays
const FADE_MS = 1200;
const LIGHT_RADIUS = 220;
// The middle of the screen holds the slide's text: code fades out there
const CLEAR_X = 0.41; // half-width of the clear area, fraction of the viewport width
const CLEAR_Y = 0.43; // half-height, fraction of the viewport height
const FRAME_MS = 1000 / 30;
const LIGHT_BOOST = 2.0; // contrast on light pages (see updateBoost)
const INK = "10,10,10";
// Must match the parallax step used in _base.scss
const PARALLAX_PER_SLIDE = 140;

const SNIPPETS = [
  `@RestController
@RequestMapping("/api/products")
public class ProductController {

  private final ProductService products;

  @GetMapping("/{id}")
  public ProductDto get(@PathVariable String id) {
    return products.findById(id)
        .orElseThrow(NotFoundException::new);
  }
}`,
  `@KafkaListener(topics = "remove-product-events")
public void onProductRemoved(ProductEvent event) {
  // clean up the images nobody uses anymore
  media.deleteByProduct(event.productId());
  log.info("media cleaned for {}", event.productId());
}`,
  `@Component({
  selector: 'app-product-card',
  templateUrl: './product-card.component.html',
})
export class ProductCardComponent {
  @Input() product!: Product;
  @Output() addToCart = new EventEmitter<Product>();

  buy(): void {
    this.addToCart.emit(this.product);
  }
}`,
  `-- liquibase formatted sql
-- changeset abdelilah:7
CREATE TABLE product_rating (
  product_id  VARCHAR(36) NOT NULL,
  user_id     VARCHAR(36) NOT NULL,
  stars       SMALLINT    NOT NULL,
  PRIMARY KEY (product_id, user_id)
);`,
  `FROM eclipse-temurin:17-jre-alpine
WORKDIR /app
COPY build/libs/service.jar app.jar
USER 10001
EXPOSE 8080
ENTRYPOINT ["java", "-jar", "app.jar"]`,
  `public class Abdelilah implements FullStackDeveloper {

  @Override
  public List<String> stack() {
    return List.of("Java", "Spring Boot",
        "Angular", "Next.js", "PostgreSQL");
  }

  @Override
  public boolean availableForHire() {
    return true; // from November 2026
  }
}`,
  `GET /products/_search
{
  "query": {
    "multi_match": {
      "query": "wireless keyboard",
      "fields": ["name^3", "description"]
    }
  }
}`,
  `@Bean
SecurityFilterChain api(HttpSecurity http) throws Exception {
  return http
      .csrf(csrf -> csrf.disable())
      .authorizeHttpRequests(auth -> auth
          .requestMatchers("/api/auth/**").permitAll()
          .anyRequest().authenticated())
      .oauth2ResourceServer(o -> o.jwt(withDefaults()))
      .build();
}`,
  `$ ./mvnw test
[INFO] Running ProductServiceTest
[INFO] Running CheckoutSessionTest
[INFO] Running MediaCleanupListenerTest
[INFO] Tests run: 48, Failures: 0
[INFO] BUILD SUCCESS`,
  `func (s *Server) getPost(w http.ResponseWriter, r *http.Request) {
	post, err := s.store.Get(r.Context(), r.PathValue("slug"))
	if errors.Is(err, ErrNotFound) {
		http.Error(w, "not found", http.StatusNotFound)
		return
	}
	writeJSON(w, http.StatusOK, post)
}`,
  `fn parse_line(line: &str) -> Option<(String, u32)> {
    let mut parts = line.split_whitespace();
    let name = parts.next()?.to_string();
    let score = parts.next()?.parse().ok()?;
    Some((name, score))
}`,
  `char *ft_strdup(const char *s)
{
    size_t  len;
    char    *copy;

    len = ft_strlen(s) + 1;
    copy = malloc(len);
    if (!copy)
        return (NULL);
    return (ft_memcpy(copy, s, len));
}`,
  `#!/usr/bin/env bash
set -euo pipefail

git pull --ff-only
npm run build
docker compose up -d --build
curl -fs https://abdelilah.bouchikhi.com > /dev/null \\
  && echo "deployed"`,
  `const res = await fetch("/api/posts?page=1");
const { posts } = await res.json();

list.innerHTML = posts
  .map((p) => "<li>" + escape(p.title) + "</li>")
  .join("");`,
  `export default function PostCard({ post }) {
  return (
    <article className="card">
      <h2>{post.title}</h2>
      <p>{post.summary}</p>
    </article>
  );
}`,
  `apiVersion: apps/v1
kind: Deployment
metadata:
  name: products-service
spec:
  replicas: 2
  template:
    spec:
      containers:
        - name: products
          image: registry/products:1.4.0
          ports:
            - containerPort: 8000`,
  `pipeline {
  agent any
  stages {
    stage('Test')   { steps { sh './mvnw test' } }
    stage('Build')  { steps { sh 'docker compose build' } }
    stage('Deploy') { steps { sh './scripts/deploy.sh' } }
  }
  post {
    failure { sh './scripts/rollback.sh' }
  }
}`,
  `.route("products", r -> r.path("/api/products/**")
    .filters(f -> f.requestRateLimiter(c -> c
        .setRateLimiter(redisRateLimiter())
        .setKeyResolver(ipKeyResolver)))
    .uri("lb://products"))`,
];

// Editor-style tab above each snippet (same order as SNIPPETS), so the stack
// reads at a glance
const SNIPPET_FILES = [
  "ProductController.java · Spring Boot",
  "MediaListener.java · Kafka",
  "product-card.component.ts · Angular",
  "007-product-rating.sql · Liquibase",
  "Dockerfile · Docker",
  "Abdelilah.java · Java",
  "search.http · OpenSearch",
  "SecurityConfig.java · Spring Security",
  "terminal · Maven",
  "server.go · Go",
  "parser.rs · Rust",
  "ft_strdup.c · C",
  "deploy.sh · Bash",
  "posts.js · JavaScript",
  "PostCard.jsx · Next.js",
  "deployment.yaml · Kubernetes",
  "Jenkinsfile · CI/CD",
  "GatewayConfig.java · Redis",
];
const PROGRAM_FILE = "loop.c · C";

// The running program: main() calls process() ten times, then starts over
const PROGRAM_MAIN = [
  "int main(void) {",
  "  int data[10];",
  "  int i = 0;",
  "  while (i < 10) {",
  "    process(&data[i]);",
  "    i++;",
  "  }",
  "}",
];
const PROGRAM_FN = ["void process(int *p) {", "  *p = *p * 2;", "}"];
const LINE_I = 2; // "int i = 0;"
const LINE_WHILE = 3;
const LINE_CALL = 4;
const LINE_INC = 5;
const STEP_MS = 1100; // one loop turn
const PAUSE_MS = 2600; // after i reaches 10
const BASE_ADDR = 0x7ffe4a20;

const KEYWORDS = new Set(
  `public private protected class interface implements extends return new final static void boolean
  if else for while throws throw import export from const let this true false null select from where
  create table primary key not insert into values get post put delete from workdir copy user expose
  entrypoint run int void char fn let mut func nil package defer impl pub struct match async await
  default function set echo pipeline stage steps agent apiversion kind spec metadata`.split(/\s+/)
);

// Splits a line into [text, weight] tokens: 0 plain, 1 strong (keywords,
// annotations), -1 faint (comments, strings).
function tokenize(line) {
  const trimmed = line.trimStart();
  if (trimmed.startsWith("//") || trimmed.startsWith("--") || trimmed.startsWith("#")) return [[line, -1]];
  const out = [];
  const re = /("[^"]*"?|'[^']*'?|@\w+|\/\/.*$|\b\w+\b|\s+|[^\w\s"'@]+)/g;
  let m;
  while ((m = re.exec(line))) {
    const t = m[0];
    let w = 0;
    if (t.startsWith('"') || t.startsWith("'") || t.startsWith("//")) w = -1;
    else if (t.startsWith("@") || KEYWORDS.has(t.toLowerCase()) || /^[A-Z][A-Z_]+$/.test(t)) w = 1;
    out.push([t, w]);
  }
  return out;
}

const rand = (a, b) => a + Math.random() * (b - a);

export default function initBackdrop() {
  const canvas = document.getElementById("backdrop");
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const reduceMotion = window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  let width = 0;
  let height = 0;
  let dpr = 1;
  let slideCount = 1;
  let running = false;
  let lastFrame = 0;
  let blocks = [];
  let programs = []; // the running while loops
  let deck = []; // shuffled snippet indexes, so neighbours differ
  const pointer = { x: -9999, y: -9999, active: false };

  // Grey code on white reads much weaker than the same code inverted on black:
  // on light pages it gets more contrast
  let boost = 1;
  function updateBoost() {
    boost = document.documentElement.dataset.theme === "dark" ? 1 : LIGHT_BOOST;
  }
  const ink = (alpha) => `rgba(${INK},${Math.min(1, alpha * boost)})`;

  function nextSnippet() {
    if (!deck.length) deck = SNIPPETS.map((_, i) => i).sort(() => Math.random() - 0.5);
    const i = deck.pop();
    return { file: SNIPPET_FILES[i] || "", lines: SNIPPETS[i].split("\n") };
  }

  // The file tab: name · technology, slightly stronger than the code, underlined
  function fileTab(name, x, y, cx, fade = 1) {
    const e = edgeFactor(x + 100, y, cx);
    if (!name || e <= 0) return;
    const ty = y - LINE_HEIGHT - 8;
    const w = ctx.measureText(name).width;
    ctx.fillStyle = ink(0.3 * fade * e);
    ctx.fillText(name, x, ty);
    ctx.fillRect(x, ty + FONT_SIZE + 5, w, 1);
  }

  // A block types one snippet, then holds, fades and restarts with another
  function restart(b, now, delay = 0) {
    const next = nextSnippet();
    b.lines = next.lines;
    b.file = next.file;
    b.total = b.lines.reduce((n, l) => n + l.length + 1, 0);
    b.cps = rand(...TYPE_CPS);
    b.start = now + delay;
    b.hold = rand(...HOLD_MS);
    b.typed = reduceMotion ? b.total : 0;
  }

  function layout(now) {
    const cols = Math.max(1, Math.round(width / BLOCK_WIDTH));
    const colW = width / cols;
    const rows = height > 760 ? 2 : 1;
    const rowH = height / rows;
    blocks = [];
    programs = [];
    for (let c = 0; c < cols; c++) {
      for (let r = 0; r < rows; r++) {
        const b = {
          x: Math.round(c * colW + rand(16, Math.max(24, colW - 360))),
          y: Math.round(r * rowH + rand(84, Math.max(92, rowH - 260))),
        };
        if ((c * rows + r) % 4 === 1 && programs.length < 2) {
          programs.push({ x: b.x, y: b.y, start: now + rand(0, 3000) });
          continue;
        }
        restart(b, now, reduceMotion ? 0 : rand(0, 6000));
        blocks.push(b);
      }
    }
  }

  // 0 in the middle of the visible screen, 1 towards its edges
  function edgeFactor(x, y, cx) {
    // phones: the text spans almost the whole width
    const clearX = window.innerWidth < 700 ? 0.62 : CLEAR_X;
    const dx = (x - cx) / (window.innerWidth * clearX);
    const dy = (y - height / 2) / (height * CLEAR_Y);
    const d = Math.hypot(dx, dy);
    return Math.min(1, Math.max(0, (d - 0.75) / 0.5));
  }

  function draw(now) {
    updateBoost();
    ctx.clearRect(0, 0, width, height);
    // centre of what is on screen, in canvas coordinates (the canvas slides)
    const cx = -parallaxOffset() + window.innerWidth / 2;
    ctx.font = `${FONT_SIZE}px "JetBrains Mono", ui-monospace, SFMono-Regular, Menlo, monospace`;
    ctx.textBaseline = "top";
    const cursorOn = Math.floor(now / 530) % 2 === 0;

    for (const pg of programs) drawProgram(pg, now, cx);

    for (const b of blocks) {
      if (now < b.start) continue;
      const elapsed = now - b.start;
      if (!reduceMotion) b.typed = Math.min(b.total, Math.floor((elapsed / 1000) * b.cps));
      const doneAt = (b.total / b.cps) * 1000;
      let fade = 1;
      if (!reduceMotion && elapsed > doneAt + b.hold) {
        fade = 1 - (elapsed - doneAt - b.hold) / FADE_MS;
        if (fade <= 0) {
          restart(b, now, rand(400, 2500));
          continue;
        }
      }

      fileTab(b.file, b.x, b.y, cx, fade);
      let left = b.typed;
      let cursor = null;
      for (let i = 0; i < b.lines.length && left >= 0; i++) {
        const y = b.y + i * LINE_HEIGHT;
        const shown = b.lines[i].slice(0, left);
        left -= b.lines[i].length + 1;
        // the flashlight: code near the pointer is a bit darker
        let light = 0;
        if (pointer.active) {
          const d = Math.hypot(pointer.x - (b.x + 120), pointer.y - y);
          light = Math.max(0, 1 - d / LIGHT_RADIUS);
        }
        let x = b.x;
        for (const [text, w] of tokenize(shown)) {
          const tw = ctx.measureText(text).width;
          const edge = edgeFactor(x + tw / 2, y, cx);
          if (edge > 0 && text.trim()) {
            const alpha = (w > 0 ? 0.2 : w < 0 ? 0.09 : 0.13) + light * 0.14;
            ctx.fillStyle = ink(alpha * fade * edge);
            ctx.fillText(text, x, y);
          }
          x += tw;
        }
        if (left < 0 || i === b.lines.length - 1) cursor = { x, y };
      }
      if (cursor && cursorOn && !reduceMotion) {
        ctx.fillStyle = ink(0.28 * fade * edgeFactor(cursor.x, cursor.y, cx));
        ctx.fillRect(cursor.x + 1, cursor.y, 7, FONT_SIZE + 2);
      }
    }
  }

  // One line of code, shaded like the snippets; strong = the line running now
  function codeLine(text, x, y, cx, strong, fade = 1) {
    let tx = x;
    for (const [t, w] of tokenize(text)) {
      const tw = ctx.measureText(t).width;
      const edge = edgeFactor(tx + tw / 2, y, cx);
      if (edge > 0 && t.trim()) {
        const alpha = (w > 0 ? 0.2 : w < 0 ? 0.09 : 0.13) + (strong ? 0.16 : 0);
        ctx.fillStyle = ink(alpha * fade * edge);
        ctx.fillText(t, tx, y);
      }
      tx += tw;
    }
    return tx;
  }

  const hex = (n) => "0x" + n.toString(16);

  function drawProgram(pg, now, cx) {
    const elapsed = Math.max(0, now - pg.start) % (10 * STEP_MS + PAUSE_MS);
    const done = elapsed >= 10 * STEP_MS;
    const i = done ? 10 : Math.floor(elapsed / STEP_MS);
    const phase = done ? 0 : (elapsed % STEP_MS) / STEP_MS;
    const active = done ? -1 : phase < 0.25 ? LINE_WHILE : phase < 0.75 ? LINE_CALL : LINE_INC;
    const fnX = pg.x + 36;
    const fnY = pg.y + PROGRAM_MAIN.length * LINE_HEIGHT + 30;

    fileTab(PROGRAM_FILE, pg.x, pg.y, cx);
    let callEnd = 0;
    PROGRAM_MAIN.forEach((line, n) => {
      const y = pg.y + n * LINE_HEIGHT;
      if (n === active) {
        const e = edgeFactor(pg.x + 120, y, cx);
        ctx.fillStyle = ink(0.05 * e);
        ctx.fillRect(pg.x - 6, y - 3, 300, LINE_HEIGHT);
      }
      const end = codeLine(line, pg.x, y, cx, n === active);
      if (n === LINE_I) codeLine(`   // i = ${i}`, end, y, cx, false);
      if (n === LINE_CALL) callEnd = end;
    });

    // process(): its parameter shows the last address it received
    const arrived = !done && phase > 0.85;
    const last = done ? 9 : arrived ? i : i - 1;
    PROGRAM_FN.forEach((line, n) => {
      const y = fnY + n * LINE_HEIGHT;
      const end = codeLine(line, fnX, y, cx, arrived && n < 2);
      if (n === 0 && last >= 0) codeLine(`   // p = ${hex(BASE_ADDR + last * 4)}`, end, y, cx, false);
    });

    // The address of data[i] drops from the call into process(int *p)
    if (!done && phase > 0.3 && phase < 0.9) {
      const q = (phase - 0.3) / 0.6;
      const x0 = callEnd + 10;
      const y0 = pg.y + LINE_CALL * LINE_HEIGHT;
      const x1 = fnX + ctx.measureText("void process(int ").width;
      const y1 = fnY + 2;
      const x = x0 + (x1 - x0) * q;
      const y = y0 + (y1 - y0) * q * q; // falls faster and faster
      const fade = Math.min(1, q * 5, (1 - q) * 6);
      const e = edgeFactor(x, y, cx);
      ctx.fillStyle = ink(0.42 * fade * e);
      ctx.fillText(hex(BASE_ADDR + i * 4), x, y);
    }
  }

  function frame(time) {
    if (!running) return;
    requestAnimationFrame(frame);
    if (time - lastFrame < FRAME_MS) return;
    lastFrame = time;
    draw(time);
  }

  function start() {
    if (running || reduceMotion || document.hidden) return;
    running = true;
    requestAnimationFrame(frame);
  }

  function resize() {
    dpr = Math.min(window.devicePixelRatio || 1, 2);
    // Wider than the viewport so it can slide sideways with the slides
    width = window.innerWidth + PARALLAX_PER_SLIDE * Math.max(slideCount - 1, 1);
    height = window.innerHeight;
    canvas.width = width * dpr;
    canvas.height = height * dpr;
    canvas.style.width = `${width}px`;
    canvas.style.height = `${height}px`;
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    const now = performance.now();
    layout(now);
    draw(now);
  }

  // Current CSS parallax offset of the canvas (animated), in px
  function parallaxOffset() {
    const t = getComputedStyle(canvas).transform;
    return t && t !== "none" ? new DOMMatrixReadOnly(t).m41 : 0;
  }

  window.addEventListener("pointermove", (e) => {
    if (e.pointerType === "touch") return;
    pointer.x = e.clientX - parallaxOffset();
    pointer.y = e.clientY;
    pointer.active = true;
  });
  const leave = () => (pointer.active = false);
  document.addEventListener("pointerleave", leave);
  window.addEventListener("blur", leave);

  document.addEventListener("slidechange", (e) => {
    const countChanged = e.detail.count !== slideCount;
    slideCount = e.detail.count;
    canvas.style.setProperty("--parallax", `${-e.detail.index * PARALLAX_PER_SLIDE}px`);
    if (countChanged) resize();
  });

  document.addEventListener("visibilitychange", () => {
    if (document.hidden) running = false;
    else start();
  });

  window.addEventListener("resize", resize);
  // Redraw once the monospace font arrives, so widths and glyphs are right
  document.fonts?.ready.then(() => draw(performance.now()));
  resize();
  start();
}
